import clsx from 'clsx';
import { Formik } from 'formik';

import { ButtonWithLoadingState } from 'components/curo-components/common/Button';
import DateInput from 'components/curo-components/common/DateInput';
import Dropdown from 'components/curo-components/common/Dropdown';
import SSNInput from 'components/curo-components/common/SSNInput';
import TextInput from 'components/curo-components/common/TextInput';
import commonStyles from 'components/curo-components/forms/Forms.module.scss';
import { FormProps } from 'components/curo-components/forms/types';
import { GoogleRecaptchaActionsType } from 'enums/GoogleRecaptchaActionsType';
import { EnvironmentVariable } from 'enums/curo/EnvironmentVariable';
import { envs } from 'envs';
import { useGetAndVerifyReCaptchaToken } from 'hooks/useGetAndVerifyReCaptchaToken';
import { createFormikHelpers } from 'utils/curo-helpers/formHelpers';
import {
  LATIN_LETTERS_SPACE_DASH_REGEX,
  NOT_CYRILLIC_REGEX,
  NOT_DIGITS_REGEX,
  formatString,
} from 'utils/curo-helpers/validation';

import styles from './ApplyForm.module.scss';
import { ApplyFormFields, IApplyFormValues, applyFormLabels, availableStates } from './constants';
import { applyFormValidationSchema } from './validationSchema';

interface IApplyFormProps extends FormProps<ApplyFormFields, IApplyFormValues> {
  footerText: string;
  isPrescreenedOfferTypeOnline: boolean;
}

const ApplyForm = ({
  isPrescreenedOfferTypeOnline,
  handleSubmit,
  initialData,
  externalErrors,
  resetExternalErrors,
  footerText,
}: IApplyFormProps) => {
  const executeRecaptcha= useGetAndVerifyReCaptchaToken();

  return (
    <Formik
      initialValues={initialData}
      validationSchema={applyFormValidationSchema}
      validateOnBlur
      validateOnChange
      validateOnMount
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ touched, errors, values, submitForm, isValid, setFieldValue, setTouched }) => {
        const handleContinue = async () => {
          if (envs[EnvironmentVariable.Environment] !== 'prod') {
            await submitForm();
            return;
          }

          const isValidToGoNext = await executeRecaptcha(
            isPrescreenedOfferTypeOnline
              ? GoogleRecaptchaActionsType.DmPersonalInfo
              : GoogleRecaptchaActionsType.ApplyProcessPersonalInfo,
          );

          if (isValidToGoNext) {
            await submitForm();
          }
        };

        const {
          handleInputChange,
          handleTouched,
          getInputErrorMessage,
          handleResetExternalError,
          hasExternalError,
        } = createFormikHelpers<ApplyFormFields, IApplyFormValues>(
          setFieldValue,
          setTouched,
          touched,
          errors,
          externalErrors,
          resetExternalErrors,
        );

        return (
          <section className={clsx(styles.formWrapper, commonStyles.formWrapper)}>
            <div className={styles.inputWrapper}>
              <TextInput
                labelTitle={applyFormLabels[ApplyFormFields.BorrowerFirstName]}
                value={values[ApplyFormFields.BorrowerFirstName]}
                onChange={handleInputChange(ApplyFormFields.BorrowerFirstName)}
                formatter={formatString(LATIN_LETTERS_SPACE_DASH_REGEX)}
                onBlur={handleTouched(ApplyFormFields.BorrowerFirstName)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerFirstName)}
                required
              />
              <TextInput
                labelTitle={applyFormLabels[ApplyFormFields.BorrowerLastName]}
                value={values[ApplyFormFields.BorrowerLastName]}
                onChange={handleInputChange(ApplyFormFields.BorrowerLastName)}
                formatter={formatString(LATIN_LETTERS_SPACE_DASH_REGEX)}
                onBlur={handleTouched(ApplyFormFields.BorrowerLastName)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerLastName)}
                required
              />
              <SSNInput
                labelTitle={applyFormLabels[ApplyFormFields.BorrowerSSN]}
                value={values[ApplyFormFields.BorrowerSSN]}
                onChange={handleInputChange(ApplyFormFields.BorrowerSSN)}
                onBlur={handleTouched(ApplyFormFields.BorrowerSSN)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerSSN)}
                placeholder="U.S. Social Security Number (###-##-####)"
                titleHint="We ask for your Social Security Number to verify your identity and obtain your credit report for processing the application."
                required
              />
              <DateInput
                name={ApplyFormFields.BorrowerDateOfBirth}
                labelTitle={applyFormLabels[ApplyFormFields.BorrowerDateOfBirth]}
                value={values[ApplyFormFields.BorrowerDateOfBirth]}
                handleInputChange={handleInputChange(ApplyFormFields.BorrowerDateOfBirth)}
                onBlur={handleTouched(ApplyFormFields.BorrowerDateOfBirth)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerDateOfBirth)}
                required
              />
              <TextInput
                labelTitle={applyFormLabels[ApplyFormFields.BorrowerStreetAddress]}
                value={values[ApplyFormFields.BorrowerStreetAddress]}
                onChange={handleInputChange(ApplyFormFields.BorrowerStreetAddress)}
                formatter={formatString(NOT_CYRILLIC_REGEX)}
                onBlur={handleTouched(ApplyFormFields.BorrowerStreetAddress)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerStreetAddress)}
                required
              />
              <TextInput
                labelTitle={applyFormLabels[ApplyFormFields.BorrowerCity]}
                value={values[ApplyFormFields.BorrowerCity]}
                onChange={handleInputChange(ApplyFormFields.BorrowerCity)}
                formatter={formatString(LATIN_LETTERS_SPACE_DASH_REGEX)}
                onBlur={handleTouched(ApplyFormFields.BorrowerCity)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerCity)}
                required
              />
              <TextInput
                labelTitle={applyFormLabels[ApplyFormFields.BorrowerZipOrPostalCode]}
                value={values[ApplyFormFields.BorrowerZipOrPostalCode]}
                onChange={handleInputChange(ApplyFormFields.BorrowerZipOrPostalCode)}
                formatter={formatString(NOT_DIGITS_REGEX)}
                maxLength={5}
                onBlur={handleTouched(ApplyFormFields.BorrowerZipOrPostalCode)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerZipOrPostalCode)}
                required
              />

              <Dropdown
                label={applyFormLabels[ApplyFormFields.BorrowerState]}
                value={values[ApplyFormFields.BorrowerState]}
                onSelect={handleInputChange(ApplyFormFields.BorrowerState)}
                onFocus={handleResetExternalError(ApplyFormFields.BorrowerState)}
                onBlur={handleTouched(ApplyFormFields.BorrowerState)}
                errorMessage={getInputErrorMessage(ApplyFormFields.BorrowerState)}
                options={availableStates}
                required
              />
            </div>

            <div className={styles.textWrapper}>
              <div className={styles.smallText}>*</div>
              <p className={styles.smallText}>{footerText}</p>
            </div>

            <ButtonWithLoadingState
              kind="primary"
              size="form"
              disabled={!isValid || hasExternalError}
              onClick={handleContinue}
            >
              Continue
            </ButtonWithLoadingState>
          </section>
        );
      }}
    </Formik>
  );
};

export default ApplyForm;
